import { useEffect, useRef } from 'react'
import { difference } from 'es-toolkit'
import { useRxCollection } from 'rxdb-hooks'

import { autofillCardPronunciationAndExample } from '~/boundedContext/list/utils/autofill-card-pronunciation-and-example'
import type { Card } from '~/db/types'
import { useIsOnline } from '~/hooks/use-is-online'
import { useAccountStore } from '~/stores/account'
import { useSettingsStore } from '~/stores/settings'
import { getJwtToken } from '~/utils/get-jwt-token'

/**
 * Layout-level side effect (null-render), mounted inside RxDbProvider like CardsGameBridge.
 *
 * Why layout-level: Autofill must outlive insert-form unmount / route changes and needs
 * `useRxCollection('cards')` access from RxDbProvider.
 *
 * Why persist card ids: Pronunciation + example autofill is async (HTTP + SSE). Closing the
 * app would otherwise drop in-flight work — ids in settings survive reload and resume when
 * online, JWT, coins, and the collection are available.
 */
export const CardsAutofillBridge = () => {
  const cardIdsRequiredAutofill = useSettingsStore(state => state.cardIdsRequiredAutofill)
  const dequeueCardIdsRequiredAutofill = useSettingsStore(state => state.dequeueCardIdsRequiredAutofill)
  const learnLanguageLabel = useSettingsStore(state => state.learnLanguage.label)
  const revalidateAccount = useAccountStore(state => state.revalidateAccount)
  const cardsCollection = useRxCollection<Card>('cards')
  const isOnline = useIsOnline()
  const cardsInProgress = useRef<string[]>([])

  useEffect(() => {
    const coins = useAccountStore.getState().coins
    const cardsNotInProgress = difference(cardIdsRequiredAutofill, cardsInProgress.current)
    if (!isOnline || !cardsCollection || !getJwtToken() || coins <= 0 || !cardsNotInProgress.length) return
    cardsInProgress.current = [...cardsInProgress.current, ...cardsNotInProgress]

    void Promise.all(
      cardsNotInProgress.map(async cardId => {
        const cardDoc = await cardsCollection.findOne(cardId).exec()

        if (!cardDoc) return dequeueCardIdsRequiredAutofill([cardId])

        if (cardDoc.pronunciation && cardDoc.examples) return dequeueCardIdsRequiredAutofill([cardId])

        await autofillCardPronunciationAndExample(cardDoc, {
          learnLanguageLabel: learnLanguageLabel ?? '',
          onExampleCoinsLimit: revalidateAccount,
        })
        dequeueCardIdsRequiredAutofill([cardId])
      }),
    ).finally(() => {
      cardsInProgress.current = difference(cardsInProgress.current, cardsNotInProgress)
    })
  }, [
    cardIdsRequiredAutofill,
    cardsCollection,
    dequeueCardIdsRequiredAutofill,
    isOnline,
    learnLanguageLabel,
    revalidateAccount,
  ])

  return null
}
